// Lomi Live — the one-shot live mock-exam competition (web client).
//
// COMPETITION semantics (unlike WeeklyMock/DailyFocus): the payload carries NO
// answers, the server owns the clock + grading, and there is NO per-question
// feedback. Answers are buffered locally and submitted in ONE batch. The
// countdown DOES auto-submit at zero.
//
// Look & feel reuses the shared exam-run aesthetic (ExamRun.css — the same
// "calm exam paper" surface as Daily Focus / Aptitude / Weekly Mock) with a
// lemon/gold "Lomi Live" accent. data-testid hooks throughout for Playwright.

import React, { useCallback, useEffect, useRef, useState } from 'react';

import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import LiveCountdown from '../components/Live/LiveCountdown';
import LiveKeepsake from '../components/Live/LiveKeepsake';
import { liveCelebrate } from '../utils/celebrate';
import '../components/ExamRun/ExamRun.css';
import './LiveExam.css';
import { engTime, ethTime } from '../utils/amharicTime';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  LiveEventInfo,
  LiveMyResult,
  LiveQuestion,
  LiveReview,
  LiveReveal,
  LiveSubmitResponse,
  liveExamAPI,
} from '../services/liveExamAPI';

// Lomi Live accent — the brand lime-green (matches the Lomi wordmark + app),
// injected into the ExamRun CSS vars.
const ACCENT = { accent: '#65a30d', soft: '#f7fee7', ring: '#d9f99d', ink: '#3f6212' };
const rootStyle = {
  ['--er-accent' as any]: ACCENT.accent,
  ['--er-accent-soft' as any]: ACCENT.soft,
  ['--er-accent-ring' as any]: ACCENT.ring,
  ['--er-accent-ink' as any]: ACCENT.ink,
} as React.CSSProperties;

const fmt = (s: number) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;
// 1→1st, 2→2nd, 3→3rd … (matches the bot /rank surface)
const ordinal = (n: number) => {
  const v = n % 100;
  const suffix = v >= 11 && v <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] || 'th';
  return `${n}${suffix}`;
};
const MEDAL = ['🥇', '🥈', '🥉'];
// Duration (ms → "M:SS") — shown so students can see the time-based tiebreak.
const fmtDur = (ms?: number | null) => (ms ? fmt(Math.round(ms / 1000)) : '');

const LiveExam: React.FC = () => {
  const { student } = useAuth();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [event, setEvent] = useState<LiveEventInfo | null>(null);
  const [phase, setPhase] = useState<'loading' | 'menu' | 'exam' | 'result' | 'review'>('loading');
  const [liveModal, setLiveModal] = useState<'rules' | 'terms' | null>(null);
  const [myResult, setMyResult] = useState<LiveMyResult | null>(null);
  const [checkMsg, setCheckMsg] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState<LiveReveal | null>(null);
  const [review, setReview] = useState<LiveReview | null>(null);

  const [subject, setSubject] = useState('');
  const [questions, setQuestions] = useState<LiveQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const [result, setResult] = useState<LiveSubmitResponse | null>(null);
  const [submitTime, setSubmitTime] = useState<Date | null>(null);

  // registration form
  const [regName, setRegName] = useState('');
  const [regStream, setRegStream] = useState<'natural' | 'social' | ''>('');
  const [regSchool, setRegSchool] = useState('');
  const [schoolSug, setSchoolSug] = useState<string[]>([]);

  // Prefill the REAL name (first_name — for prize delivery, NOT the username) +
  // stream from the logged-in profile, if present.
  useEffect(() => {
    if (!student) return;
    setRegName((n) => n || student.first_name || '');
    setRegStream((s) => s || (student.stream === 'social' ? 'social' : student.stream === 'natural' ? 'natural' : ''));
  }, [student]);

  const qStartRef = useRef<number>(Date.now());
  const timesRef = useRef<Record<number, number>>({});
  const finishing = useRef(false);
  const deadlineRef = useRef<number>(0); // wall-clock ms when the timer hits 0
  const lsKey = (s: string) => `lomi-live:${event?.slug || 'ev'}:${s}`;

  // ---------------- load event ----------------
  const loadEvent = useCallback(async () => {
    try {
      const e = await liveExamAPI.event();
      setEvent(e);
      if (e.status === 'completed') {
        setReveal(await liveExamAPI.reveal(10).catch(() => null));
        // a registered student opening AFTER the reveal lands straight on their
        // own results — fetch them up front (and celebrate a win).
        if (e.registration) {
          const mr = await liveExamAPI.myResult().catch(() => null);
          if (mr?.ready) {
            setMyResult(mr);
            if (mr.prize) void liveCelebrate('lemon');
          }
        }
      }
      setPhase('menu');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'The live event is not open right now.');
      setPhase('menu');
    }
  }, []);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  const openReview = async (s: string) => {
    setBusy(true);
    try {
      setReview(await liveExamAPI.review(s));
      setPhase('review');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Review not available yet.');
    } finally {
      setBusy(false);
    }
  };

  // "See my results" from the all-done screen — state-aware: shows the reveal once
  // the switch is flipped, else a friendly wait + community nudge.
  const checkResults = async () => {
    setBusy(true);
    setCheckMsg('');
    try {
      const r = await liveExamAPI.myResult();
      if (r.ready) {
        setMyResult(r);
        if (r.prize) void liveCelebrate('lemon');
      } else {
        setCheckMsg("Results aren't out yet — champions revealed at 1 PM today. Hang tight, or join the community below 🍋");
      }
    } catch {
      setCheckMsg('Could not check results just now — try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  // Personal reveal renderer — used on the all-done screen (after the switch flips)
  // AND in the COMPLETED view (auto-loaded).
  const renderReveal = (r: LiveMyResult) => {
    if (r.disqualified) {
      return (
        <div className="lv-st-reveal" data-testid="live-reveal-disqualified">
          <div className="lv-st-alldone-h" style={{ marginBottom: 10 }}>
            📋 Your entries are under review
          </div>
          <p className="lv-muted" style={{ lineHeight: 1.5 }}>
            They aren’t included in the final rankings. If you believe this is a
            mistake, please reach out and our team will look into it.
          </p>
          <a
            className="lv-st-alldone-cta"
            href="https://t.me/lomicommunity"
            target="_blank"
            rel="noopener noreferrer"
          >
            ❓ Contact us
          </a>
        </div>
      );
    }
    return (
    <div className="lv-st-reveal" data-testid="live-reveal">
      {r.prize ? (
        <div className="lv-st-prizewin">
          <div className="lv-st-prizewin-h">🏆 You won {r.prize.total.toLocaleString()} birr!</div>
          <div className="lv-st-prizewin-lines">
            {r.prize.lines.map((l, i) => (
              <div key={i}><span>{l.label}</span><b>{l.cash.toLocaleString()}</b></div>
            ))}
          </div>
          <div className="lv-st-prizewin-note">We'll reach out to send it your way 🎉 (subject to verification)</div>
        </div>
      ) : (
        <div className="lv-st-alldone-h" style={{ marginBottom: 10 }}>🎉 Your Lomi Live results</div>
      )}
      {r.overall && (
        <div className="lv-st-rankline lv-st-rankline--hero" data-testid="live-overall-rank">
          🌍 Overall — <b>{ordinal(r.overall.rank)}</b> of {r.overall.of} · {r.overall.correct} correct
          {r.overall.time_ms ? <> · ⏱️ {fmtDur(r.overall.time_ms)}</> : null}
        </div>
      )}
      <div className="lv-st-scorecard">
        {(r.subjects || []).map((s) => (
          <div key={s.subject} className="lv-st-scorerow">
            <span className="lv-st-score-subj">{s.rank <= 3 ? MEDAL[s.rank - 1] + ' ' : ''}{s.subject}</span>
            <span className="lv-st-score-num">
              <b>{ordinal(s.rank)}</b> of {s.of} · {s.correct} correct
              {s.time_ms ? <> · ⏱️ {fmtDur(s.time_ms)}</> : null}
            </span>
          </div>
        ))}
      </div>
      <div className="lv-muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
        ⏱️ is your time — ties are broken by time (faster wins).
      </div>
      {r.stream && (
        <div className="lv-st-rankline">{r.stream.name === 'natural' ? '🌿' : '🏛️'} {r.stream.name} stream — <b>{ordinal(r.stream.rank)}</b> of {r.stream.of}</div>
      )}
      {r.school && <div className="lv-st-rankline">🏫 Your school — <b>{ordinal(r.school.rank)}</b></div>}
      <LiveKeepsake
        name={r.name || student?.first_name || 'Lomi Star'}
        school={event?.registration?.school_name}
        line={r.prize ? `Won ${r.prize.total.toLocaleString()} birr` : `${(r.subjects || []).reduce((a, s) => a + s.correct, 0)} correct`}
        dateLabel={event?.event_date ? new Date(event.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'Africa/Addis_Ababa' }) : 'Lomi Live'}
      />
      <div className="lv-st-links">
        {(event?.registration?.subjects || []).map((s) => (
          <button key={s} className="lv-st-link" onClick={() => openReview(s)}>📋 Review {s}</button>
        ))}
      </div>
      <a className="lv-st-alldone-cta" href="https://t.me/lomicommunity" target="_blank" rel="noopener noreferrer">💬 Join the community</a>
    </div>
    );
  };

  // ---------------- countdown (auto-submits at 0) ----------------
  const doSubmit = useCallback(async () => {
    if (finishing.current) return;
    finishing.current = true;
    setBusy(true);
    const cur = questions[idx];
    if (cur && answers[cur.seq]) {
      timesRef.current[cur.seq] = Math.max(0, Math.round(Date.now() - qStartRef.current));
    }
    const payload = questions
      .filter((q) => answers[q.seq])
      .map((q) => ({ seq: q.seq, chosen: answers[q.seq], time_spent_ms: timesRef.current[q.seq] }));
    try {
      const res = await liveExamAPI.submit(subject, payload);
      try {
        localStorage.removeItem(lsKey(subject));
      } catch {
        /* ignore */
      }
      setResult(res);
      setSubmitTime(res.submitted_at ? new Date(res.submitted_at) : new Date());
      setPhase('result');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Submit failed — please try again.');
      finishing.current = false;
    } finally {
      setBusy(false);
    }
  }, [questions, answers, idx, subject]);

  // Wall-clock anchored: each tick recomputes from the server-set deadline (and on
  // tab re-focus), so backgrounding the tab can't drift or "pause" the displayed
  // clock. The server (started_at) stays the final authority; this keeps the
  // visible timer honest and fires the auto-submit at the real zero.
  useEffect(() => {
    if (phase !== 'exam') return;
    const tick = () => {
      const rem = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
      setRemaining(rem);
      if (rem <= 0) void doSubmit();
    };
    tick();
    const id = window.setInterval(tick, 1000);
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [phase, doSubmit]);

  // ---- survive refresh / closed tab: persist locally + autosave to server ----
  const answersRef = useRef<Record<number, string>>(answers);
  useEffect(() => {
    answersRef.current = answers;
    if (phase === 'exam' && subject) {
      try {
        localStorage.setItem(lsKey(subject), JSON.stringify(answers));
      } catch {
        /* storage full / disabled — server autosave still covers it */
      }
    }
  }, [answers, phase, subject]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNow = useCallback(() => {
    if (phase !== 'exam' || !subject) return;
    const a = answersRef.current;
    const payload = questions
      .filter((q) => a[q.seq])
      .map((q) => ({ seq: q.seq, chosen: a[q.seq], time_spent_ms: timesRef.current[q.seq] }));
    if (payload.length) liveExamAPI.save(subject, payload).catch(() => {});
  }, [phase, subject, questions]);

  useEffect(() => {
    if (phase !== 'exam') return;
    const id = window.setInterval(saveNow, 15000);
    const onHide = () => {
      if (document.visibilityState === 'hidden') saveNow();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [phase, saveNow]);

  // ---------------- registration ----------------
  useEffect(() => {
    if (regSchool.trim().length < 2) {
      setSchoolSug([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setSchoolSug(await liveExamAPI.schoolSuggest(regSchool.trim()));
      } catch {
        setSchoolSug([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [regSchool]);

  const register = async () => {
    if (!regStream) {
      setError('Pick your stream first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await liveExamAPI.register({
        stream: regStream,
        name: regName.trim() || undefined,
        school_name: regSchool.trim() || undefined,
      });
      await loadEvent();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Registration failed — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const startSubject = async (s: string) => {
    setBusy(true);
    setError(null);
    try {
      const started = await liveExamAPI.start(s);
      if (started.status === 'submitted') {
        setSubject(s);
        setResult({
          submitted: true,
          subject: s,
          correct_count: started.correct_count, // undefined until the slot closes
          total_questions: started.total_questions,
          late: false,
        });
        setPhase('result');
        return;
      }
      const pl = await liveExamAPI.payload(s);
      finishing.current = false;
      timesRef.current = {};
      setConfirmSubmit(false);
      setSubject(s);
      setQuestions(pl.questions);
      // restore buffered answers on resume — prefer the SERVER's saved answers
      // (authoritative + survives cross-surface/cross-device), else localStorage.
      let restored: Record<number, string> = {};
      if (started.saved && Object.keys(started.saved).length) {
        for (const [k, v] of Object.entries(started.saved)) restored[Number(k)] = v;
      } else {
        try {
          restored = JSON.parse(localStorage.getItem(lsKey(s)) || '{}') || {};
        } catch {
          restored = {};
        }
      }
      setAnswers(restored);
      setIdx(0);
      const rem0 = started.remaining_seconds ?? pl.budget_seconds ?? 1200;
      deadlineRef.current = Date.now() + rem0 * 1000;
      setRemaining(rem0);
      qStartRef.current = Date.now();
      setPhase('exam');
    } catch (err: any) {
      setError(err?.response?.data?.detail || `Couldn't start ${s}.`);
    } finally {
      setBusy(false);
    }
  };

  const choose = (seq: number, label: string) => {
    timesRef.current[seq] = Math.max(0, Math.round(Date.now() - qStartRef.current));
    setAnswers((a) => ({ ...a, [seq]: label }));
  };

  const goTo = (n: number) => {
    if (n < 0 || n >= questions.length) return;
    qStartRef.current = Date.now();
    setIdx(n);
  };

  const answeredCount = Object.keys(answers).length;

  // Always confirm — submitting is final and can't be undone.
  const onSubmitClick = () => setConfirmSubmit(true);

  // ---------------- render ----------------
  const shell = (children: React.ReactNode, testid: string) => (
    <div className="er-root" style={rootStyle} data-testid={testid}>
      <main className="er-main">{children}</main>
    </div>
  );

  if (phase === 'loading') {
    return shell(<div style={{ color: dark ? '#aab1ba' : '#64748b' }}>Loading…</div>, 'live-loading');
  }

  // ---- RESULTS (event completed) ----
  if (phase === 'menu' && event?.status === 'completed') {
    const reg = event?.registration;
    const winRow = (e: { rank: number; name?: string; correct?: number; school?: string | null; time_ms?: number }) => (
      <div key={`${e.rank}-${e.name}`} className="lv-row">
        <span>
          <span className="lv-rank">{e.rank <= 3 ? MEDAL[e.rank - 1] : `${e.rank}.`}</span> {e.name}
          {e.time_ms ? <span className="lv-muted"> · ⏱️ {fmtDur(e.time_ms)}</span> : null}
        </span>
        <strong>{e.correct}</strong>
      </div>
    );
    return shell(
      <>
        {myResult?.ready && (myResult.disqualified || (myResult.subjects?.length ?? 0) > 0) && (
          <div className="lv-reveal-wrap">{renderReveal(myResult)}</div>
        )}
        <div style={{ fontSize: '1.1rem', fontWeight: 800, margin: '16px 0 2px' }}>
          🏆 Full leaderboard
        </div>
        <p className="lv-muted" style={{ margin: '0 0 6px', fontSize: '0.8rem' }}>
          Lomi Live Mock Exam · June 20
        </p>
        {reveal && (
          <>
            <p className="lv-muted" data-testid="live-stats">
              {reveal.stats.participants} students · {reveal.stats.submitted} papers
            </p>
            {reveal.overall?.length ? (
              <div className="er-card lv-card" data-testid="live-board-overall">
                <div className="er-context">🌍 Overall</div>
                {reveal.overall.slice(0, 20).map(winRow)}
              </div>
            ) : null}
            {(['natural', 'social'] as const).map((st) =>
              reveal.grand[st].length ? (
                <div key={st} className="er-card lv-card" data-testid={`live-grand-${st}`}>
                  <div className="er-context">{st === 'natural' ? '🌿 Natural' : '🏛️ Social'} Grand Champion</div>
                  {reveal.grand[st].slice(0, 3).map(winRow)}
                </div>
              ) : null,
            )}
            {Object.entries(reveal.subjects).map(([s, entries]) =>
              entries.length ? (
                <div key={s} className="er-card lv-card" data-testid={`live-board-${s}`}>
                  <div className="er-context">{s}</div>
                  {entries.slice(0, 20).map(winRow)}
                </div>
              ) : null,
            )}
          </>
        )}
        {reg && (
          <div className="er-card lv-card">
            <div className="er-context">Review your answers</div>
            {reg.subjects.map((s) => (
              <button key={s} data-testid={`live-review-${s}`} disabled={busy} onClick={() => openReview(s)} className="lv-btn-soft">
                📋 {s}
              </button>
            ))}
          </div>
        )}
        {error && <div data-testid="live-error" className="lv-err">{error}</div>}
      </>,
      'live-results-page',
    );
  }

  // ---- REVIEW (per-question) ----
  if (phase === 'review' && review) {
    return shell(
      <div data-testid="live-review">
        <h1 className="lv-h1">📋 {review.subject} — {review.correct_count}/{review.total}</h1>
        {review.items.map((it) => (
          <div key={it.seq} className="er-card lv-card" data-testid={`live-review-item-${it.seq}`}>
            <div className="er-context">{it.is_correct ? '✅ correct' : '❌ incorrect'} · Q{it.seq}</div>
            <div className="er-q"><MarkdownRenderer>{it.stem}</MarkdownRenderer></div>
            <div className="er-options" style={{ marginTop: 12 }}>
              {it.options.map((o) => (
                <div
                  key={o.label}
                  className="er-opt"
                  data-state={o.is_correct ? 'correct' : o.is_chosen ? 'wrong' : undefined}
                  style={{ cursor: 'default' }}
                >
                  <span className="er-badge">{o.label}.</span>
                  <span className="er-opt-text">
                    <MarkdownRenderer>{o.text}</MarkdownRenderer>
                    {o.is_chosen ? <em className="lv-muted"> — your answer</em> : null}
                  </span>
                </div>
              ))}
            </div>
            {it.explanation && (
              <div className="er-explain" style={{ marginTop: 10 }}>
                <MarkdownRenderer>{it.explanation}</MarkdownRenderer>
              </div>
            )}
          </div>
        ))}
        <button data-testid="live-review-back" onClick={() => setPhase('menu')} className="er-next">
          ◀ Back to results
        </button>
      </div>,
      'live-review-page',
    );
  }

  // ---- MENU / LOBBY ----
  if (phase === 'menu') {
    const reg = event?.registration;
    const live = event?.status === 'live';
    const allDone =
      !!reg &&
      (reg.subjects || []).length > 0 &&
      (reg.subjects || []).every((s) => event?.my_attempts?.[s]?.status === 'submitted');
    const whenLabel = event?.event_date
      ? new Date(event.event_date).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone: 'Africa/Addis_Ababa',
        })
      : '';
    const streamSubs = regStream && event ? event.streams[regStream] || [] : [];
    return (
      <div className="lv-stage" data-testid="live-menu">
        <div className="lv-st-orb lv-st-orb1" />
        <div className="lv-st-orb lv-st-orb2" />
        <div className="lv-st-inner">
          <div className="lv-st-bar">
            <img src="/logo192.png" alt="Lomi" className="lv-st-logo" />
            <div className="lv-st-wm">Lomi <span>Live</span></div>
            <div className="lv-st-live"><i />LIVE</div>
          </div>

          <div className={`lv-st-grid${reg && live ? ' lv-st-grid--compete' : ''}`}>
          <div className="lv-st-left">
          <div className="lv-st-hero" data-testid="live-hero">
            <div className="lv-st-eyebrow" data-testid="live-when">
              Live ESSLCE mock-exam{whenLabel ? ` · ${whenLabel}` : ''}
            </div>
            <h1 className="lv-st-headline">Lomi stars, head to head</h1>
            <div className="lv-st-sub">Top your subjects, win your stream, take the crown.</div>
          </div>

          {event && (
            <LiveCountdown
              stage
              countdown={event.countdown}
              nowUtc={event.now_utc}
              onElapsed={() => void loadEvent()}
            />
          )}

          {event && event.registered_count > 0 && (
            <div className="lv-st-proof" data-testid="live-fomo">
              🔥 <b>{event.registered_count.toLocaleString()}</b> students registered
            </div>
          )}
          </div>

          <div className="lv-st-right">
          {error && <div className="lv-st-err" data-testid="live-error">{error}</div>}

          {!reg ? (
            <div className="lv-st-card" data-testid="live-register-form">
              <h2 className="lv-st-cardh">Claim your spot</h2>
              <div className="lv-st-cardsub">One tap. Compete for real prizes.</div>

              <label className="lv-st-lab">Your name</label>
              <input
                data-testid="live-name-input"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                placeholder="Your name"
                className="lv-st-inp"
              />

              <label className="lv-st-lab">Your stream</label>
              <div className="lv-st-chips">
                {(['natural', 'social'] as const).map((s) => (
                  <button
                    key={s}
                    data-testid={`live-stream-${s}`}
                    onClick={() => setRegStream(s)}
                    className="lv-st-chip"
                    data-active={regStream === s}
                  >
                    {s === 'natural' ? '🌿 Natural' : '🏛️ Social'}
                  </button>
                ))}
              </div>
              {regStream && event && (
                <div className="lv-st-preview" data-testid="live-subject-preview">
                  You'll compete in <b>{streamSubs.join(' · ')}</b>
                </div>
              )}

              <label className="lv-st-lab">Your school</label>
              <input
                data-testid="live-school-input"
                value={regSchool}
                onChange={(e) => setRegSchool(e.target.value)}
                placeholder="Start typing your school…"
                className="lv-st-inp"
              />
              {schoolSug.length > 0 && (
                <div data-testid="live-school-suggestions" className="lv-st-suggest">
                  {schoolSug.map((s) => (
                    <div
                      key={s}
                      data-testid="live-school-opt"
                      onClick={() => { setRegSchool(s); setSchoolSug([]); }}
                      className="lv-st-suggest-item"
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}

              <button
                data-testid="live-register-btn"
                disabled={busy || !regStream}
                onClick={register}
                className="lv-st-cta"
              >
                {busy ? 'Registering…' : 'Register · ይመዝገቡ'}
              </button>
            </div>
          ) : (
            <div className="lv-st-card" data-testid="live-registered">
              <div className="lv-st-reg-badge">✓ You're in — {reg.subjects.join(' · ')}</div>
              {live && allDone ? (
                myResult?.ready ? (
                  renderReveal(myResult)
                ) : (
                  <div className="lv-st-alldone" data-testid="live-alldone">
                    <div className="lv-st-alldone-h">🎉 You've completed Lomi Live!</div>
                    <div className="lv-st-alldone-sub">All {reg.subjects.length} subjects submitted &amp; locked in.</div>
                    <div className="lv-st-donerow">
                      {reg.subjects.map((s) => <span key={s} className="lv-st-donepill">✓ {s}</span>)}
                    </div>
                    <div className="lv-st-alldone-note">🏆 Champions revealed at <b>1 PM today</b>.</div>
                    <div className="lv-st-alldone-actions">
                      <button className="lv-st-alldone-cta" disabled={busy} data-testid="live-check-results" onClick={checkResults}>
                        {busy ? 'Checking…' : '📊 See my results'}
                      </button>
                      <a className="lv-st-alldone-cta lv-st-alldone-cta--ghost" href="https://t.me/lomicommunity" target="_blank" rel="noopener noreferrer">💬 Join community</a>
                    </div>
                    {checkMsg && <div className="lv-st-alldone-msg">{checkMsg}</div>}
                  </div>
                )
              ) : live ? (
                <>
                  <div className="lv-st-cardsub">Tap a subject to start. One attempt each.</div>
                  {reg.subjects.map((s) => {
                    const slot = event?.slots.find((sl) => sl.subject === s);
                    const ph = slot?.phase ?? 'upcoming';
                    const mine = event?.my_attempts?.[s];
                    if (mine?.status === 'in_progress' && ph === 'open') {
                      return (
                        <button key={s} data-testid={`live-resume-${s}`} disabled={busy} onClick={() => startSubject(s)} className="lv-st-subject lv-st-subject--live">
                          <span>▶ Resume {s}</span>
                          <span className="lv-st-pill">in progress</span>
                        </button>
                      );
                    }
                    if (mine?.status === 'submitted' || mine?.status === 'in_progress') {
                      return (
                        <div key={s} data-testid={`live-done-${s}`} className="lv-st-subject lv-st-subject--off">
                          <span>✓ {s}</span>
                          <span className="lv-st-pill">{mine.correct != null ? `${mine.correct}/${mine.total}` : 'submitted'}</span>
                        </div>
                      );
                    }
                    if (ph === 'open') {
                      return (
                        <button key={s} data-testid={`live-start-${s}`} disabled={busy} onClick={() => startSubject(s)} className="lv-st-subject lv-st-subject--live">
                          <span>▶ {s}</span>
                          <span className="lv-st-pill">🟢 LIVE · {slot?.budget_min ?? 20}m</span>
                        </button>
                      );
                    }
                    return (
                      <div key={s} data-testid={`live-slot-${s}`} className="lv-st-subject lv-st-subject--off">
                        <span>{s}</span>
                        <span className="lv-st-pill-dim">{ph === 'upcoming' ? `opens ${slot ? engTime(slot.start_eat) : ''}` : 'closed'}</span>
                      </div>
                    );
                  })}
                  <div className="lv-st-note">🏆 Champions revealed at 1 PM today</div>
                </>
              ) : (
                <>
                  <div className="lv-st-cardsub">You're registered. See you Saturday morning 🍋</div>
                  <div className="lv-st-schedrows" data-testid="live-schedule">
                    {(event?.slots || [])
                      .filter((sl) => reg.subjects.includes(sl.subject))
                      .map((sl) => (
                        <div key={sl.subject} className="lv-st-schedrow">
                          <span>{sl.subject}</span>
                          <span className="lv-st-pill-dim">{engTime(sl.start_eat)} · {ethTime(sl.start_eat)}</span>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="lv-st-links">
            <button className="lv-st-link" onClick={() => setLiveModal('rules')}>📋 Rules &amp; how it works</button>
            <span className="lv-st-link-sep">·</span>
            <button className="lv-st-link" onClick={() => setLiveModal('terms')}>Terms</button>
            <span className="lv-st-link-sep">·</span>
            <a className="lv-st-link" href="https://t.me/lomicommunity" target="_blank" rel="noopener noreferrer">❓ Need help?</a>
          </div>
          </div>
          </div>

          {!(reg && live) && (
          <div className="lv-st-prizebox">
            <div className="lv-st-prize-label">🏆 Cash prizes at every level</div>

            <div className="lv-st-prize-grp">
              <div className="lv-st-prize-sec">Top 3 in every subject</div>
              <div className="lv-st-prize-chips">
                <div className="lv-st-pchip"><b>2,000</b><span>🥇 1st</span></div>
                <div className="lv-st-pchip"><b>1,500</b><span>🥈 2nd</span></div>
                <div className="lv-st-pchip"><b>1,000</b><span>🥉 3rd</span></div>
              </div>
            </div>

            <div className="lv-st-prize-grp">
              <div className="lv-st-prize-sec">Stream champions</div>
              <div className="lv-st-prize-chips">
                <div className="lv-st-pchip"><b>3,000</b><span>🌿 Natural</span></div>
                <div className="lv-st-pchip"><b>3,000</b><span>🏛️ Social</span></div>
              </div>
            </div>

            <div className="lv-st-prize-grp">
              <div className="lv-st-prize-sec">Overall champion</div>
              <div className="lv-st-prize-chips">
                <div className="lv-st-pchip"><b>+1,000</b><span>👑 Best across all</span></div>
              </div>
            </div>

            <div className="lv-st-prize-ceiling">Win as big as <b>10,000 birr</b> · እስከ 10 ሺ ብር</div>
          </div>
          )}

          <div className="lv-st-foot">🍋 ሎሚ · Champions revealed live at 1 PM today</div>

          {liveModal && (
            <div className="lv-modal-overlay" onClick={() => setLiveModal(null)} data-testid="live-modal">
              <div className="lv-modal" onClick={(e) => e.stopPropagation()}>
                <button className="lv-modal-x" aria-label="Close" onClick={() => setLiveModal(null)}>✕</button>
                {liveModal === 'rules' ? (
                  <>
                    <h2 className="lv-modal-h">📋 Rules of the game</h2>
                    <div className="lv-st-rule"><span>🎯</span><span><b>3 subjects, one shot each</b> — Math (30 Qs · 40 min), English (35 · 30 min), and your stream Bio/Geo (35 · 30 min).</span></div>
                    <div className="lv-st-rule"><span>🥇</span><span><b>Most correct wins</b> — and ties go to whoever <b>submits first</b>. Speed matters.</span></div>
                    <div className="lv-st-rule"><span>🔒</span><span><b>One fair shot</b> — one attempt per account, answers shuffled. Looking things up only costs you time.</span></div>
                    <div className="lv-st-rule"><span>💰</span><span><b>Cash, per subject</b> — 1st <b>2,000</b>, 2nd <b>1,500</b>, 3rd <b>1,000</b>. Both stream champions <b>3,000</b>, overall crown <b>+1,000</b> → win <b>10,000 birr</b>.</span></div>
                    <div className="lv-st-rule"><span>📅</span><span><b>Sequential schedule</b> — Math 10:00 → English 10:50 → Geo ∥ Bio 11:30. Each subject opens on its clock.</span></div>
                    <div className="lv-st-rule"><span>🏆</span><span><b>Scores hidden</b> until each subject closes; champions revealed at <b>1 PM</b> — about an hour after.</span></div>
                    <div className="lv-st-rule"><span>🏫</span><span>Represent your <b>school</b> — there's a school-vs-school crown too.</span></div>
                  </>
                ) : (
                  <>
                    <h2 className="lv-modal-h">Terms &amp; conditions</h2>
                    <ul className="lv-terms">
                      <li>Open to registered Lomi students. <b>One entry per verified account.</b></li>
                      <li>Each student competes in their three stream subjects, <b>one attempt each</b>.</li>
                      <li>Ranking is by most correct; ties are broken by total time (fastest first).</li>
                      <li>Winners are <b>subject to verification</b> (account age, activity, anti-cheat checks). Suspected cheating or multi-accounting is disqualified.</li>
                      <li>Cash prizes are paid to the registered phone after verification. <b>Lomi's decisions are final.</b></li>
                      <li>This is a <b>practice mock exam</b> — not affiliated with or endorsed by the official ESSLCE / Ministry of Education.</li>
                      <li>By entering you agree your first name and school may appear on leaderboards and recognition posts.</li>
                      <li>Lomi may adjust the schedule or rules for fairness or technical reasons.</li>
                    </ul>
                  </>
                )}
                <button className="lv-st-cta" style={{ marginTop: 18 }} onClick={() => setLiveModal(null)}>Got it</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- EXAM ----
  if (phase === 'exam') {
    const q = questions[idx];
    const low = remaining <= 60;
    const pct = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;
    const unanswered = questions.map((qq, i) => (answers[qq.seq] ? -1 : i)).filter((i) => i >= 0);
    return (
      <div className="er-root" style={rootStyle} data-testid="live-exam">
        <header className="er-header">
          <div className="er-railwrap">
            <div className="er-rail" role="progressbar" aria-valuenow={answeredCount} aria-valuemin={0} aria-valuemax={questions.length}>
              <div className="er-rail-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="er-count" data-testid="live-progress">{subject} · Question {idx + 1} of {questions.length}</div>
          </div>
          <div className="er-right">
            <span data-testid="live-timer" style={{ color: low ? (dark ? '#ff6b6b' : '#dc2626') : undefined, fontVariantNumeric: 'tabular-nums' }}>⏳ {fmt(remaining)}</span>
          </div>
        </header>

        <main className="er-main">
          <div className="er-card" data-testid="live-question">
            <div className="er-q"><MarkdownRenderer>{q.stem}</MarkdownRenderer></div>
            <div className="er-options">
              {q.options.map((o) => (
                <button
                  key={o.label}
                  className="er-opt"
                  data-state={answers[q.seq] === o.label ? 'selected' : undefined}
                  data-testid={`live-option-${o.label}`}
                  onClick={() => choose(q.seq, o.label)}
                >
                  <span className="er-badge">{o.label}.</span>
                  <span className="er-opt-text"><MarkdownRenderer>{o.text}</MarkdownRenderer></span>
                </button>
              ))}
            </div>
          </div>

          {/* question navigator — see answered (filled) vs unanswered at a glance, jump to any */}
          <div className="lv-dots" data-testid="live-dots">
            {questions.map((qq, i) => (
              <button
                key={qq.seq}
                data-testid={`live-dot-${i}`}
                className="lv-dot"
                data-state={answers[qq.seq] ? 'done' : 'todo'}
                data-current={i === idx ? 'true' : undefined}
                onClick={() => goTo(i)}
                aria-label={`Question ${i + 1}${answers[qq.seq] ? ' (answered)' : ''}`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <div className="lv-nav">
            <button data-testid="live-prev" disabled={idx === 0} onClick={() => goTo(idx - 1)} className="lv-btn-ghost lv-nav-btn">
              ◀ Back
            </button>
            <button data-testid="live-next" disabled={idx === questions.length - 1} onClick={() => goTo(idx + 1)} className="lv-btn-ghost lv-nav-btn">
              Next ▶
            </button>
          </div>

          {confirmSubmit ? (
            <div className="er-card lv-card lv-confirm" data-testid="live-confirm">
              <div className="lv-confirm-h">Submit {subject} — final?</div>
              <p className="lv-confirm-warn">
                Once you submit, <b>you can't come back or change anything</b>. This is your one shot.
              </p>
              {unanswered.length > 0 && (
                <>
                  <p className="lv-confirm-sub">
                    <b>{unanswered.length}</b> question{unanswered.length === 1 ? '' : 's'} still unanswered
                    {' '}(they count as wrong). Tap to jump back:
                  </p>
                  <div className="lv-unanswered" data-testid="live-unanswered">
                    {unanswered.map((i) => (
                      <button
                        key={i}
                        data-testid={`live-unanswered-${i}`}
                        className="lv-dot"
                        data-state="todo"
                        onClick={() => { setConfirmSubmit(false); goTo(i); }}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button
                  data-testid="live-confirm-no"
                  onClick={() => setConfirmSubmit(false)}
                  className="lv-btn-ghost"
                  style={{ flex: 1 }}
                >
                  ◀ Keep going
                </button>
                <button data-testid="live-confirm-yes" disabled={busy} onClick={() => void doSubmit()} className={`er-next${unanswered.length ? ' lv-confirm-warnbtn' : ''}`} style={{ flex: 1, marginTop: 0 }}>
                  {busy ? 'Submitting…' : unanswered.length ? `⚠️ Submit — ${unanswered.length} blank` : `Submit ${subject}`}
                </button>
              </div>
            </div>
          ) : (
            <button
              data-testid="live-submit"
              disabled={busy}
              onClick={onSubmitClick}
              className="lv-submit"
              data-complete={answeredCount === questions.length ? 'true' : undefined}
            >
              {busy
                ? 'Submitting…'
                : answeredCount === questions.length
                ? `✓ Submit ${subject} — all ${questions.length} answered`
                : `⚠️ ${questions.length - answeredCount} unanswered · Submit ${subject}`}
            </button>
          )}
        </main>
      </div>
    );
  }

  // ---- RESULT (calm per-subject confirmation — no score, no celebration) ----
  const regSubjects: string[] = event?.registration?.subjects || [];
  const schedOrder = (event?.slots || []).map((s) => s.subject).filter((s) => regSubjects.includes(s));
  const curIdx = schedOrder.indexOf(subject);
  const nextSubject = curIdx >= 0 && curIdx < schedOrder.length - 1 ? schedOrder[curIdx + 1] : null;
  const nextSlot = nextSubject ? (event?.slots || []).find((s) => s.subject === nextSubject) : null;
  const submitLabel = submitTime
    ? submitTime.toLocaleTimeString('en-GB', {
        timeZone: 'Africa/Addis_Ababa', hour: '2-digit', minute: '2-digit', second: '2-digit',
      })
    : null;
  return shell(
    <div data-testid="live-result">
      <div className="er-card lv-card lv-submitted">
        <div className="lv-submitted-check" aria-hidden>✓</div>
        <h2 className="lv-submitted-h" data-testid="live-submitted">{subject} submitted</h2>
        {submitLabel && (
          <div className="lv-submitted-time">Recorded at <b>{submitLabel}</b> EAT</div>
        )}
        {result?.late && (
          <div className="lv-submitted-late">⏰ Submitted after time — flagged for review</div>
        )}
        <div className="lv-submitted-lock">🔒 Your answers are locked — they can't be changed.</div>
        {nextSubject ? (
          <div className="lv-submitted-next">
            Up next: <b>{nextSubject}</b>
            {nextSlot ? (nextSlot.phase === 'open' ? ' — open now' : ` · opens ${engTime(nextSlot.start_eat)}`) : ''}
          </div>
        ) : (
          <div className="lv-submitted-next">That's all your subjects — nicely done. 🍋</div>
        )}
        <div className="lv-submitted-note">
          Scores show after all subjects close · champions revealed at <b>1 PM</b> today 🏆
        </div>
      </div>

      <button data-testid="live-back-menu" onClick={() => { setResult(null); void loadEvent(); }} className="er-next" style={{ marginTop: 16 }}>
        {nextSubject ? 'Back to menu' : 'Done'}
      </button>
    </div>,
    'live-result-page',
  );
};

export default LiveExam;
